import { createContext, useContext } from 'react';
import { useState } from 'react';

import { ChakraProvider } from '@chakra-ui/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { StateMachineProvider } from 'little-state-machine';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { queryClient } from '@/bootstrap/react-query';
import { ErrorBoundary } from '@/components/ErrorBoundary/ErrorBoundary';
import { SmartBannerProvider } from '@/components/SmartBanner/SmartBannerProvider';
import '@/lib/dayjs/config';
import '@/lib/i18n/client';
import { AVAILABLE_LANGUAGES } from '@/lib/i18n/constants';
import '@/lib/store';
import theme from '@/theme';

export function Providers({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation();
  return (
    <AppContextProvider>
      <ErrorBoundary>
        <ChakraProvider
          toastOptions={{ defaultOptions: { position: 'top-right' } }}
          theme={{
            ...theme,
            direction:
              AVAILABLE_LANGUAGES.find(({ key }) => key === i18n.language)
                ?.dir ?? 'ltr',
          }}
        >
          <StateMachineProvider>
            <HelmetProvider>
              <QueryClientProvider client={queryClient}>
                <SmartBannerProvider>{children}</SmartBannerProvider>
              </QueryClientProvider>
            </HelmetProvider>
          </StateMachineProvider>
        </ChakraProvider>
      </ErrorBoundary>
    </AppContextProvider>
  );
}

interface AppContextType {
  isShowBanner: boolean;
  setIsShowBanner: (value: boolean) => void;
}

export const AppContext = createContext<AppContextType>({
  isShowBanner: true,
  setIsShowBanner: () => {},
});

export const useAppContext = () => useContext(AppContext);

export function AppContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isShowBanner, setIsShowBanner] = useState(true);

  return (
    <AppContext.Provider value={{ isShowBanner, setIsShowBanner }}>
      {children}
    </AppContext.Provider>
  );
}
